import { usePdaZoneStore } from '@/store/pda-zone.store';
import { usePersonnelTrackingStore } from '@/store/personnel-tracking.store';
import { useEndSession } from '@/hooks/usePersonnelTracking';
import { useElapsedTime } from '@/hooks/useElapsedTime';
import Button from '@/components/common/Button';
import { FOREGROUND_ONLY_TRACKING_SHORT } from '@/constants/honest-ui';

// ---------------------------------------------------------------------------
// Track type display config (duplicated from PersonnelTracking page for
// the compact field bar -- avoids a cross-page import)
// ---------------------------------------------------------------------------

const TRACK_TYPE_DISPLAY: Record<string, { label: string; color: string }> = {
  route_clearing: { label: 'Route Clearing', color: '#f97316' },
  patrol: { label: 'Patrol', color: '#3b82f6' },
  search_rescue: { label: 'Search & Rescue', color: '#ef4444' },
  damage_survey: { label: 'Damage Survey', color: '#8b5cf6' },
  supply_run: { label: 'Supply Run', color: '#10b981' },
  other: { label: 'Other', color: '#6b7280' },
};

// ---------------------------------------------------------------------------
// Shared accuracy dot
// ---------------------------------------------------------------------------

function AccuracyDot({ accuracy }: { accuracy: number | null }) {
  const dotColor = accuracy != null
    ? accuracy < 10 ? 'bg-green-500' : accuracy < 30 ? 'bg-yellow-500' : 'bg-red-500'
    : 'bg-gray-400';
  return <span className={`h-2.5 w-2.5 rounded-full animate-pulse ${dotColor}`} />;
}

// ---------------------------------------------------------------------------
// PDA Zone Tracker (UNCHANGED)
// ---------------------------------------------------------------------------

function PdaZoneTracker() {
  const { pointCount, lastAccuracy, stopTracking } = usePdaZoneStore();

  const accuracyLabel = lastAccuracy != null
    ? lastAccuracy < 10 ? 'Excellent' : lastAccuracy < 30 ? 'Good' : 'Low'
    : 'Waiting...';

  const isFiltered = lastAccuracy != null && lastAccuracy > 50;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AccuracyDot accuracy={lastAccuracy} />
          <span className="text-xs font-medium text-theme-primary">GPS Tracking</span>
          <span className="text-xs text-theme-tertiary">{pointCount} pts</span>
          <span className="text-xs text-theme-tertiary">
            {accuracyLabel}{lastAccuracy != null ? ` (${Math.round(lastAccuracy)}m)` : ''}
          </span>
          {isFiltered && (
            <span className="text-xs font-medium text-red-500 dark:text-red-400">Paused</span>
          )}
        </div>
        <Button
          onClick={stopTracking}
          variant="danger"
          size="sm"
        >
          Stop
        </Button>
      </div>
      {isFiltered && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
          GPS accuracy too low ({Math.round(lastAccuracy!)}m). Move to open sky. Points resume when accuracy &lt; 50m.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Personnel Track Recorder (replaces GpsTrackRecorder)
// ---------------------------------------------------------------------------

function PersonnelTrackRecorder() {
  const store = usePersonnelTrackingStore();
  const endSession = useEndSession();

  const { callsign, trackType, startedAt, pointCount, lastAccuracy, currentSessionId, stopGeolocationWatch } = store;

  const elapsed = useElapsedTime(startedAt ?? null);

  const trackConfig = trackType ? TRACK_TYPE_DISPLAY[trackType] : null;
  const typeLabel = trackConfig?.label || trackType || null;
  const typeColor = trackConfig?.color || '#6b7280';

  const accuracyValue = lastAccuracy ?? null;
  const isFiltered = accuracyValue != null && accuracyValue > 50;

  const handleStop = async () => {
    await stopGeolocationWatch();
    if (currentSessionId) {
      endSession.mutate(currentSessionId);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <AccuracyDot accuracy={accuracyValue} />
          {callsign && (
            <span className="text-xs font-semibold text-theme-primary truncate max-w-[100px]">
              {callsign}
            </span>
          )}
          {typeLabel && (
            <span
              className="text-xs font-semibold px-1.5 py-0.5 rounded shrink-0"
              style={{ backgroundColor: typeColor, color: '#fff' }}
            >
              {typeLabel}
            </span>
          )}
          <span className="text-xs font-mono text-theme-primary">{elapsed}</span>
          <span className="text-xs text-theme-tertiary">{pointCount ?? 0} pts</span>
          {accuracyValue != null && (
            <span className="text-xs text-theme-tertiary">
              {Math.round(accuracyValue)}m
            </span>
          )}
        </div>
        <Button
          onClick={handleStop}
          disabled={endSession.isPending}
          variant="danger"
          size="sm"
        >
          Stop
        </Button>
      </div>
      {isFiltered && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
          GPS accuracy too low ({Math.round(accuracyValue!)}m). Move to open sky. Points resume when accuracy &lt; 50m.
        </p>
      )}
      {/* P0-12: a persistent reminder for the whole session, not just the start dialog. */}
      <p className="text-xs text-theme-tertiary mt-1">{FOREGROUND_ONLY_TRACKING_SHORT}</p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function GpsTracker() {
  const pdaIsTracking = usePdaZoneStore((s) => s.isTracking);
  const personnelIsTracking = usePersonnelTrackingStore((s) => s.isTracking);

  if (!pdaIsTracking && !personnelIsTracking) return null;

  return (
    <div
      className="fixed left-2 right-2 z-[35] bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-lg border border-theme-primary px-3 py-2"
      style={{ top: 'calc(3rem + env(safe-area-inset-top) + 0.5rem)' }}
    >
      {pdaIsTracking ? <PdaZoneTracker /> : <PersonnelTrackRecorder />}
    </div>
  );
}
